/**
 * A capture/enrichment tier bulb — off/dim/on/bright, picked on the bulb itself. A pick belongs
 * to this bulb alone (`bulbTier.ts`): it is remembered per `surface`, sent with whatever the
 * bulb is attached to, and neither changes nor is changed by any other bulb or by the account's
 * capture tier in Settings (#82). That tier is only the starting point, and what the watch gets.
 *
 * A `SplitButton` wearing a lightbulb, same shape as `AiBreakdownButton`. Without `onPress`
 * **the main face is inert**: on the Add card the bulb only chooses a tier for the entry, and
 * the Add button is still what submits.
 *
 * With `onPress` the face is live: the Inbox's Create-task form wears this beside its submit
 * button, and pressing the bulb hands the item to the AI. It is then disabled while this bulb's
 * own tier is Off — pressing would only be refused — but the flyout stays usable, since that is
 * how it gets turned back on.
 *
 * It is not drawn at all on a server with no AI to ask: AI switched off, or no model assigned yet.
 */

import { useId } from "react";
import type { CaptureTier } from "../api";
import { useAiConfigured } from "../aiConfigured";
import { useBulbTier, type BulbSurface } from "../bulbTier";
import { Lightbulb } from "./icons";
import { SplitButton, type SplitButtonItem } from "./SplitButton";

const TIER_ORDER: readonly CaptureTier[] = ["off", "dim", "on", "bright"];

const TIER_LABEL: Record<CaptureTier, string> = {
  off: "Off",
  dim: "Dim",
  on: "On",
  bright: "Bright",
};

const TIER_BLURB: Record<CaptureTier, string> = {
  off: "No AI for this",
  dim: "Fast, always available, rougher",
  on: "The default once something's configured",
  bright: "Best. Slowest or costliest, worth it when it matters",
};

export function CaptureTierPicker({
  surface,
  onPress,
  pressLabel = "Refine with AI",
  pressDisabled = false,
}: {
  /** Which bulb this is: its pick is remembered under this name and is independent of every
   * other surface's, and of the account's capture tier (`bulbTier.ts`). */
  surface: BulbSurface;
  onPress?: () => void;
  /** The face's accessible name when it is live; the tier is appended. */
  pressLabel?: string;
  /** The live face has nothing to hand over yet (an empty form). */
  pressDisabled?: boolean;
}) {
  const { tier, setTier } = useBulbTier(surface);
  const configured = useAiConfigured();
  const anchorName = `--capture-tier-${useId().replace(/:/g, "")}`;

  const items: SplitButtonItem[] = TIER_ORDER.map((candidate) => ({
    key: candidate,
    // Reuses `.ai-breakdown-menu-item`'s layout (icon + wrapped text) rather than a second
    // copy of the same two rules under a new name — see styles.css's note by that class.
    label: (
      <span className="ai-breakdown-menu-item">
        <Lightbulb level={candidate} size={14} />
        {TIER_LABEL[candidate]} - {TIER_BLURB[candidate]}
      </span>
    ),
    onSelect: () => setTier(candidate),
  }));

  // A server with AI switched off, or no model assigned, can only refuse the bulb (`aiConfigured.ts`).
  if (!configured) return null;

  return (
    <span className="capture-tier-picker">
      <SplitButton
        label={<Lightbulb level={tier ?? "off"} size={14} />}
        mainAriaLabel={
          onPress
            ? `${pressLabel} (${tier ? TIER_LABEL[tier] : "loading"})`
            : `AI tier for new entries: ${tier ? TIER_LABEL[tier] : "loading"}`
        }
        onClick={onPress ?? (() => {})}
        mainDisabled={onPress !== undefined && (tier === "off" || pressDisabled)}
        items={items}
        anchorName={anchorName}
      />
    </span>
  );
}
